import React, { useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import '../../style/ReportLostPetFound.css';

const MAP_CONTAINER_STYLE = { width: '100%', height: '300px' };
const DEFAULT_CENTER = { lat: 41.3851, lng: 2.1734 }; // fallback

const ReportLostPetFound = () => {
    const { t } = useTranslation(); // Initialize useTranslation
    const { id } = useParams();
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [foundDate, setFoundDate] = useState('');
    const [foundTime, setFoundTime] = useState('');
    const [coordinates, setCoordinates] = useState(null); // { lat, lng }
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const navigate = useNavigate();

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places', 'maps'],
    });

    const autocompleteRef = useRef(null);
    const mapRef = useRef(null);

    const onLoadAutocomplete = useCallback((autocomplete) => {
        autocompleteRef.current = autocomplete;
    }, []);

    const onPlaceChanged = useCallback(() => {
        const ac = autocompleteRef.current;
        if (!ac) return;
        const place = ac.getPlace();

        if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            setCoordinates({ lat, lng });
        }
    }, []);

    const handleReportFound = async () => {
        try {
            if (!foundDate || !foundTime || !coordinates) {
                setError('Please provide all required information.');
                return;
            }

            const foundAt = new Date(`${foundDate}T${foundTime}`);
            const foundLocation = {
                type: 'Point',
                coordinates: [coordinates.lng, coordinates.lat],
            };

            
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            const response = await axios.patch(
                `${baseUrl}/api/pets/${id}/found`,
                { foundAt, foundLocation },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.status === 200) {
                setSuccess(true);
                setTimeout(() => navigate(`/pet/${id}`), 2000); // Redirect after success
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message);
        }
    };

    const onMapLoad = useCallback((map) => (mapRef.current = map), []);

    if (loadError) return <div>{t('reportLostPetFoundPage.mapError')}</div>;
    if (!isLoaded) return <div>{t('reportLostPetFoundPage.mapLoading')}</div>;

    return (
        <div className="report-lost-pet-found-page">
            <h1>{t('reportLostPetFoundPage.title')}</h1>
            {error && <p style={{ color: 'red' }}>{t('reportLostPetFoundPage.error')}</p>}
            {success ? (
                <p style={{ color: 'green' }}>{t('reportLostPetFoundPage.success')}</p>
            ) : (
                <>
                    <div>
                        <label>{t('reportLostPetFoundPage.dateFoundLabel')}</label>
                        <input
                            type="date"
                            value={foundDate}
                            onChange={(e) => setFoundDate(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <label>{t('reportLostPetFoundPage.timeFoundLabel')}</label>
                        <input
                            type="time"
                            value={foundTime}
                            onChange={(e) => setFoundTime(e.target.value)}
                            required
                        />
                    </div>

                    <div>
                        <label>{t('reportLostPetFoundPage.locationFoundLabel')}</label>
                        <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                            <input
                                type="text"
                                placeholder={t('reportLostPetFoundPage.locationPlaceholder')}
                                className="location-input"
                            />
                        </Autocomplete>
                    </div>

                    <div className="map-preview">
                        <GoogleMap
                            mapContainerStyle={MAP_CONTAINER_STYLE}
                            center={coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : DEFAULT_CENTER}
                            zoom={coordinates ? 15 : 12}
                            onLoad={onMapLoad}
                        >
                            {coordinates && <Marker position={{ lat: coordinates.lat, lng: coordinates.lng }} />}
                        </GoogleMap>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <button onClick={handleReportFound}>{t('reportLostPetFoundPage.confirmButton')}</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReportLostPetFound;
